import { FC } from "react";

import { usePathname } from "next/navigation";

import { getTranslation } from "@/common/helpers/getStaticProps";
import { ServerSideContext } from "@/common/types/app";
import { ZeffyHead } from "@/components/Head/ZeffyHead";
import { FoundationProfile } from "@/features/GrantFinder/FoundationProfile/FoundationProfile";
import { GrantFinderLayout } from "@/features/GrantFinder/GrantFinderLayout/GrantFinderLayout";
import { getLastPathSegment } from "@/features/GrantFinder/helpers/routeId";
import { createGrantFinderProxyClient, GRANT_FINDER_HUB_REDIRECT } from "@/features/GrantFinder/helpers/serverProxy";

interface FoundationProfilePageProps {
  ein: string;
}

const FoundationProfilePage: FC<FoundationProfilePageProps> = ({ ein: einProp }) => {
  const pathname = usePathname();
  const ein = getLastPathSegment(pathname) ?? einProp;

  return (
    <>
      <ZeffyHead
        title="Foundation profile | Zeffy Grant Finder"
        description="Open grant opportunities offered by this foundation."
        noIndex={false}
      />
      <GrantFinderLayout slots={{ main: <FoundationProfile ein={ein} /> }} />
    </>
  );
};

export const getServerSideProps = async (context: ServerSideContext<{ ein: string }>) => {
  const ein = context.params?.ein;
  if (!ein || typeof ein !== "string") {
    return { notFound: true as const };
  }

  try {
    const funder = await createGrantFinderProxyClient().grantFinder_getFunderByEin.query({ ein });
    if (!funder) {
      return GRANT_FINDER_HUB_REDIRECT;
    }
  } catch {
    // Transient resolver failure: render the page rather than caching a permanent redirect away from a valid funder.
  }

  const translations = await getTranslation(context.locale);
  return { props: { ...translations, ein } };
};

export default FoundationProfilePage;
